import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { getGoal, setGoal } from "../lib/goal";
import { PLANS } from "../data/readingPlans";
import { Repeat, BookMarked, ListChecks } from "lucide-react";

const MODES = [
  { id: "plan", label: "Follow a plan", icon: ListChecks, hint: "Track a reading plan" },
  { id: "recurring", label: "Recurring", icon: Repeat, hint: "Bloom on a rhythm" },
  { id: "cumulative", label: "Whole Bible", icon: BookMarked, hint: "One big goal" },
];
const PERIODS = ["day", "week", "month"];
const PRESETS = [1, 3, 4, 7, 10];

export default function GoalDialog({ open, onOpenChange, onSaved }) {
  const [g, setG] = useState(getGoal());

  const save = () => {
    setGoal(g);
    onOpenChange(false);
    onSaved && onSaved();
  };

  const set = (patch) => setG((prev) => ({ ...prev, ...patch }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="goal-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Set your bloom goal</DialogTitle>
          <DialogDescription>Choose how your Focus Journey tree grows.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = g.mode === m.id;
            return (
              <button
                key={m.id}
                data-testid={`goal-mode-${m.id}`}
                onClick={() => set({ mode: m.id })}
                className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-center transition-all ${
                  active ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                }`}
              >
                <Icon className="h-5 w-5 text-primary" />
                <span className="text-xs font-semibold">{m.label}</span>
                <span className="text-[10px] text-muted-foreground">{m.hint}</span>
              </button>
            );
          })}
        </div>

        {g.mode === "recurring" && (
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium">Chapters per period</p>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((n) => (
                  <button
                    key={n}
                    data-testid={`goal-target-${n}`}
                    onClick={() => set({ target: n })}
                    className={`h-10 w-10 rounded-lg border text-sm font-semibold transition-all ${
                      g.target === n ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={g.target}
                  data-testid="goal-target-input"
                  onChange={(e) => set({ target: Math.max(1, Math.min(50, +e.target.value || 1)) })}
                  className="h-10 w-16 rounded-lg border border-border bg-transparent px-2 text-center text-sm"
                />
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium">Per</p>
              <div className="grid grid-cols-3 gap-2">
                {PERIODS.map((p) => (
                  <button
                    key={p}
                    data-testid={`goal-period-${p}`}
                    onClick={() => set({ period: p })}
                    className={`rounded-lg border py-2 text-sm capitalize transition-all ${
                      g.period === p ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {g.mode === "cumulative" && (
          <div>
            <p className="mb-2 text-sm font-medium">Goal — total chapters</p>
            <div className="flex flex-wrap gap-2">
              {[30, 100, 260, 1189].map((n) => (
                <button
                  key={n}
                  data-testid={`goal-cumulative-${n}`}
                  onClick={() => set({ target: n })}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-all ${
                    g.target === n ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"
                  }`}
                >
                  {n === 1189 ? "Whole Bible" : n}
                </button>
              ))}
              <input
                type="number"
                min="1"
                max="1189"
                value={g.target}
                data-testid="goal-cumulative-input"
                onChange={(e) => set({ target: Math.max(1, Math.min(1189, +e.target.value || 1)) })}
                className="h-10 w-24 rounded-lg border border-border bg-transparent px-2 text-center text-sm"
              />
            </div>
          </div>
        )}

        {g.mode === "plan" && (
          <div>
            <p className="mb-2 text-sm font-medium">Track a reading plan</p>
            <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
              {PLANS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`goal-plan-${p.id}`}
                  onClick={() => set({ planId: p.id })}
                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-all ${
                    g.planId === p.id ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                  }`}
                >
                  <span className="truncate font-medium">{p.title}</span>
                  <span className="ml-2 shrink-0 text-xs text-muted-foreground">{p.days.length}d</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={save}
          data-testid="goal-save"
          className="mt-2 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
        >
          Save goal
        </button>
      </DialogContent>
    </Dialog>
  );
}
